/**
 * PlayerCard — one card per active (on-floor) player.
 *
 * Layout:
 *  ┌──────────────────────────────────────┐
 *  │ ●avatar  #N  Name           ✕  ⋯    │  ← header
 *  │           02:34   F:1                │
 *  ├──────────────────────────────────────┤
 *  │ [ children: action buttons grid ]    │  ← caller-injected
 *  └──────────────────────────────────────┘
 *
 * The action buttons are passed as children so the screen can compose
 * different button sets per mode (live vs video) without forking this card.
 */

import React from 'react';
import { Box, Avatar, Typography, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useStatsTheme, withAlpha } from '../theme/statsTheme';
import { formatTime } from '../../utils/timeUtils';

function FoulPills({ count = 0, max = 5 }) {
  const theme = useStatsTheme();
  return (
    <Box sx={{ display: 'flex', gap: 0.4 }} aria-label={`${count} fouls`}>
      {Array.from({ length: max }).map((_, i) => {
        const filled = i < count;
        const danger = count >= 4 && filled;
        return (
          <Box
            key={i}
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: filled
                ? danger
                  ? theme.actionColors.missed
                  : theme.actionColors.warning
                : withAlpha(theme.text.muted, 0.4),
            }}
          />
        );
      })}
    </Box>
  );
}

export default function PlayerCard({
  player,
  timeOnFloorSeconds = 0,
  fouls = 0,
  onSubstitute,
  onMore,
  highlight = false,
  compact = false,
  children,
}) {
  const theme = useStatsTheme();

  if (!player) return null;

  const avatarSize = compact ? 30 : 38;
  const headerPad = compact ? 0.7 : 1.2;
  const bodyPad = compact ? 0.6 : 1;
  const nameSize = compact ? 12 : 14;
  const iconBtn = compact ? 24 : 28;

  return (
    <Box
      sx={{
        backgroundColor: theme.surfaces.floating,
        borderRadius: `${theme.radius.lg}px`,
        border: `1px solid ${highlight ? theme.accentBorder : theme.surfaces.border}`,
        boxShadow: theme.shadows.md,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: `border-color ${theme.motion.fast}, transform ${theme.motion.fast}`,
        '&:hover': {
          borderColor: theme.accentBorder,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: headerPad,
          borderBottom: `1px solid ${theme.surfaces.divider}`,
          backgroundColor: withAlpha(theme.accent, 0.06),
        }}
      >
        <Avatar
          src={player.avatarUrl}
          alt={player.nom || ''}
          sx={{
            width: avatarSize,
            height: avatarSize,
            border: `2px solid ${theme.accentBorder}`,
            backgroundColor: theme.surfaces.elevated,
          }}
        >
          {player.nom?.charAt(0).toUpperCase() || '?'}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
            {player.dorsal && (
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: theme.accent,
                  letterSpacing: '0.04em',
                }}
              >
                #{player.dorsal}
              </Typography>
            )}
            <Typography
              noWrap
              sx={{
                fontSize: nameSize,
                fontWeight: 700,
                color: theme.text.primary,
                flex: 1,
                minWidth: 0,
              }}
            >
              {player.nom}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.3 }}>
            <Typography
              sx={{
                fontSize: 11,
                color: theme.text.secondary,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatTime(timeOnFloorSeconds || 0)}
            </Typography>
            <FoulPills count={fouls} />
          </Box>
        </Box>

        {onMore && (
          <Tooltip title="More actions">
            <IconButton
              size="small"
              onClick={() => onMore(player)}
              aria-label="more actions"
              sx={{
                width: iconBtn,
                height: iconBtn,
                color: theme.text.secondary,
                '&:hover': { backgroundColor: withAlpha(theme.text.muted, 0.2) },
              }}
            >
              <MoreHorizIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}

        {onSubstitute && (
          <Tooltip title="Substitute out">
            <IconButton
              size="small"
              onClick={() => onSubstitute(player)}
              aria-label="substitute out"
              sx={{
                width: iconBtn,
                height: iconBtn,
                color: theme.actionColors.missed,
                '&:hover': { backgroundColor: withAlpha(theme.actionColors.missed, 0.2) },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {children && (
        <Box sx={{ p: bodyPad, display: 'flex', flexDirection: 'column', gap: compact ? 0.5 : 0.8 }}>
          {children}
        </Box>
      )}
    </Box>
  );
}
